import { createRouter, createWebHistory } from 'vue-router';
import SignIn from '@/presentation/pages/authentication/SignIn.vue';
import { useCompanyStore } from '@/stores/CompanyStore.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import Middleware from './middleware';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/sign-in',
      name: 'SignIn',
      meta: { title: ['features.main_menu.sign_in'] },
      component: SignIn,
      beforeEnter: Middleware.isGuest,
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      meta: { title: ['features.main_menu.password_forgot'] },
      component: () => import('@/presentation/pages/ForgotPassword.vue'),
      beforeEnter: Middleware.isGuest,
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      meta: { title: ['features.main_menu.password_reset'] },
      component: () => import('@/presentation/pages/ResetPassword.vue'),
      beforeEnter: Middleware.isGuest,
    },
    {
      path: '/',
      name: 'Dashboard',
      meta: { title: ['global.dashboard'] },
      component: () => import('@/presentation/pages/dashboard/Dashboard.vue'),
      beforeEnter: Middleware.isFullySignedIn,
    },
    {
      path: '/health',
      name: 'Health',
      meta: { title: ['global.health'] },
      component: () => import('@/presentation/pages/health/Health.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      props: () => {
        return {
          companyId: useSessionStore().company.getId(),
          company: useCompanyStore().company,
        };
      },
    },
    {
      path: '/issues',
      name: 'Issues',
      meta: { title: ['glossary.issue', 2] },
      component: () => import('@/presentation/pages/resources/IssuesPage.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      props: () => {
        return { company: useSessionStore().company };
      },
    },
    {
      path: '/performances',
      name: 'PerformancesMonitoring',
      meta: { title: ['glossary.performance', 2] },
      component: () =>
        import('@/presentation/pages/resources/PerformancesPage.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      props: () => {
        return { company: useSessionStore().company };
      },
    },
    {
      path: '/tickets',
      name: 'Tickets',
      meta: { title: ['glossary.ticket', 2] },
      component: () => import('@/presentation/pages/tickets/Tickets.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      props: () => {
        return { company: useSessionStore().company };
      },
      children: [
        {
          name: 'TicketsPage',
          path: '',
          meta: { title: ['features.main_menu.all_tickets'] },
          component: () =>
            import('@/presentation/pages/resources/TicketsPage.vue'),
          props: () => {
            return { company: useSessionStore().company };
          },
        },
        {
          name: 'SingleTicket',
          path: ':ticketId',
          meta: { title: ['features.main_menu.single_ticket'] },
          component: () =>
            import('@/presentation/pages/tickets/SingleTicket.vue'),
          props: (route) => {
            return {
              company: useCompanyStore().company,
              ticketId: parseInt(route.params.ticketId as string),
              admin: useSessionStore().user.admin,
            };
          },
        },
      ],
    },
    {
      path: '/reports',
      name: 'Reports',
      meta: { title: ['global.report', 2] },
      component: () => import('@/presentation/pages/reports/Reports.vue'),
      props: () => {
        return { company: useSessionStore().company };
      },
    },
    {
      path: '/plants',
      component: () => import('@/presentation/pages/plants/Plants.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      children: [
        {
          name: 'AllPlants',
          path: '',
          component: () =>
            import('@/presentation/pages/resources/PlantsPage.vue'),
          meta: { title: ['features.main_menu.all_plants'] },
          props: () => {
            return {
              company: useCompanyStore().company,
              adminMode: useSessionStore().adminMode,
            };
          },
        },
        {
          name: 'SinglePlant',
          path: ':plantId',
          meta: { title: ['features.main_menu.single_plant'] },
          component: () =>
            import('@/presentation/pages/plants/SinglePlant.vue'),
          props: (route) => {
            return {
              company: useCompanyStore().company,
              plantId: parseInt(route.params.plantId as string),
              admin: useSessionStore().user.admin,
              adminMode: useSessionStore().adminMode,
            };
          },
        },
      ],
    },
    {
      path: '/users',
      component: () => import('@/presentation/pages/users/Users.vue'),
      meta: { title: ['global.user', 2] },
      beforeEnter: Middleware.isFullySignedIn,
      children: [
        {
          name: 'AllUsers',
          path: '',
          component: () => import('@/presentation/pages/users/AllUsers.vue'),
          props: () => {
            return {
              companyId: useSessionStore().company.id,
              admin: useSessionStore().adminMode,
            };
          },
        },
        {
          name: 'CreateUser',
          path: 'create',
          component: () => import('@/presentation/pages/users/CreateUser.vue'),
          props: () => {
            return { companyId: useSessionStore().company.id };
          },
        },
      ],
    },
    {
      path: '/settings',
      name: 'Settings',
      meta: { title: ['global.setting', 2] },
      component: () => import('@/presentation/pages/settings/Settings.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      props: () => {
        return { company: useCompanyStore().company };
      },
      children: [
        {
          path: 'tags',
          name: 'Tags',
          component: () => import('@/presentation/pages/tags/TagsPage.vue'),
          beforeEnter: Middleware.isFullySignedIn,
          props: () => {
            return { company: useSessionStore().company };
          },
        },
        {
          path: 'custom-fields',
          name: 'Custom Fields',
          component: () =>
            import('@/presentation/pages/custom-fields/CustomFieldsPage.vue'),
          beforeEnter: Middleware.isFullySignedIn,
          props: () => {
            return { company: useCompanyStore().company };
          },
        },
      ],
    },
    {
      path: '/account',
      name: 'Account',
      meta: { title: ['global.account'] },
      component: () => import('@/presentation/pages/account/Account.vue'),
      beforeEnter: Middleware.isFullySignedIn,
    },
    {
      path: '/support',
      name: 'Support',
      meta: { title: ['global.support'] },
      component: () => import('@/presentation/pages/support/Support.vue'),
      props: () => {
        return { company: useSessionStore().company };
      },
    },
    {
      path: '/error',
      name: 'ErrorPage',
      meta: { title: 'Oops!' },
      component: () => import('@/presentation/pages/error/ErrorPage.vue'),
    },
    {
      path: '/changelog',
      name: 'Changelog',
      meta: { title: ['features.main_menu.whats_new'] },
      component: () => import('@/presentation/pages/changelog/Changelog.vue'),
      props: (route) => {
        return { debug: route.query.debug };
      },
    },
    {
      path: '/api',
      name: 'ApiPage',
      component: () => import('@/presentation/pages/api/ApiPage.vue'),
    },
    ///
    // ADMIN END POINTS
    ///
    {
      path: '/admin/companies',
      component: () => import('@/presentation/pages/companies/Companies.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      meta: {
        linkText: 'Companies',
      },
      children: [
        {
          name: 'AllCompanies',
          path: '',
          component: () =>
            import('@/presentation/pages/companies/AllCompanies.vue'),
          meta: { title: 'All Companies' },
        },
        {
          name: 'CreateCompany',
          path: 'create',
          component: () =>
            import('@/presentation/pages/companies/CreateCompany.vue'),
          meta: { title: 'Create Company' },
        },
        {
          name: 'EditCompany',
          path: ':companyId',
          component: () =>
            import('@/presentation/pages/companies/EditCompany.vue'),
          props: (route) => {
            return {
              companyId: parseInt(route.params.companyId as string),
            };
          },
          meta: { title: 'Edit Company' },
        },
      ],
    },
    {
      path: '/admin/tickets',
      component: () => import('@/presentation/pages/tickets/Tickets.vue'),
      beforeEnter: Middleware.isFullySignedIn,
      children: [
        {
          name: 'AdminTicketsPage',
          path: '',
          component: () =>
            import('@/presentation/pages/resources/AdminTicketsPage.vue'),
          props: () => {
            return { company: useCompanyStore().company };
          },
        },
      ],
    },
  ],
});

export default router;
